import {
  ScoreCardApiRes,
  ScoreCardDataParsedRes,
  RegionWiseModalsParsedRes,
  RegionWiseModalsGraphApiRes,
  RegionWiseModalsDataInterface,
  QuarterData,
  AwardInterface,
  QuarterWiseModalsParsedRes,
  QuarterWiseModalsGraphApiRes,
  IndividualIndicatorsGraphData,
  IndividualIndicatorsGraphApiRes,
  IndividualIndicatorsGraphParsedRes,
  IndividualIndicatorsGraphSeriesData,
  AwardsInterfaces,
  PerformanceIndicatorsGraphData,
  PerformanceIndicatorsGraphApiRes,
  PerformanceIndicatorsGraphParsedRes,
  HitMapTableApiRes,
  HitMapTableParsedRes,
  DataParametersType,
  FinalDataInterface,
  PerformanceTableApiRes,
  PerformanceTableResData,
  PerformanceTableParsedRes,
  PerformanceTableDataParameters,
} from "../types/dashboard";

// Define the interface for the dashboard parser
interface DashboardParser {
  getScoreCardParser: (res: ScoreCardApiRes) => ScoreCardDataParsedRes;
  getRegionWiseMedalsGraphParser: (
    res: RegionWiseModalsGraphApiRes
  ) => RegionWiseModalsParsedRes;
  getQuarterWiseMedalGraphParser: (
    res: QuarterWiseModalsGraphApiRes
  ) => QuarterWiseModalsParsedRes[];
  getIndividualIndicatorsGraphParser: (
    res: IndividualIndicatorsGraphApiRes
  ) => IndividualIndicatorsGraphParsedRes;
  getPerformanceTableParser: (
    res: PerformanceTableApiRes
  ) => PerformanceTableParsedRes;
  getHeatMapTableParser: (res: HitMapTableApiRes) => HitMapTableParsedRes;
  getPerformanceIndicatorsGraphParser: (
    res: PerformanceIndicatorsGraphApiRes
  ) => PerformanceIndicatorsGraphParsedRes;
}

// Initialize the dashboardParser object
const dashboardParser: DashboardParser = {
  getScoreCardParser: (res: ScoreCardApiRes): ScoreCardDataParsedRes => {
    let totalCenters: number = 0;
    let data: AwardsInterfaces[] = [];

    if (Array.isArray(res?.data?.awards) && res?.data?.awards.length > 0) {
      totalCenters = res.data.totalCenters;
      data = res.data.awards;
    }

    const cardDetails: AwardsInterfaces[] = data.map((cardInfo) => {
      return {
        awardId: cardInfo?.awardId,
        awardName: cardInfo.awardName || "",
        count: cardInfo.count || 0,
        percentage: cardInfo.percentage || 0,
      };
    });

    return { cardDetails, totalCenters };
  },

  getRegionWiseMedalsGraphParser: (
    res: RegionWiseModalsGraphApiRes
  ): RegionWiseModalsParsedRes => {
    const data: RegionWiseModalsDataInterface[] =
      Array.isArray(res.data) && res.data.length > 0 ? res.data : [];

    const barColorCode = {
      Platinum: "#8047E7",
      Gold: "#E7B147",
      Silver: "#BEBEBE",
      Bronze: "#E79447",
    };

    const categories: string[] = data.map((data) => {
      return data.region;
    });

    const regionWiseModalSeriesData: any = [
      {
        name: "Platinum",
        data: data.map((quarter) => {
          const award = quarter.awards.find((a) => a.awardName === "Platinum");
          return {
            y: award ? award.count : 0,
            percentage: award ? award.percentage : 0,
          };
        }),
        color: barColorCode.Platinum,
      },
      {
        name: "Gold",
        data: data.map((quarter) => {
          const award = quarter.awards.find((a) => a.awardName === "Gold");
          return {
            y: award ? award.count : 0,
            percentage: award ? award.percentage : 0,
          };
        }),
        color: barColorCode.Gold,
      },
      {
        name: "Silver",
        data: data.map((quarter) => {
          const award = quarter.awards.find((a) => a.awardName === "Silver");
          return {
            y: award ? award.count : 0,
            percentage: award ? award.percentage : 0,
          };
        }),
        color: barColorCode.Silver,
      },
      {
        name: "Bronze",
        data: data.map((quarter) => {
          const award = quarter.awards.find((a) => a.awardName === "Bronze");
          return {
            y: award ? award.count : 0,
            percentage: award ? award.percentage : 0,
          };
        }),
        color: barColorCode.Bronze,
      },
    ];

    return { categories, regionWiseModalSeriesData };
  },

  getQuarterWiseMedalGraphParser: (
    res: QuarterWiseModalsGraphApiRes
  ): QuarterWiseModalsParsedRes[] => {
    let data: QuarterData[] = [];

    if (Array.isArray(res?.data) && res?.data?.length > 0) {
      data = res.data;
    }

    // Dummy api response:
    // const data = [
    //   {
    //     region: "West",
    //     awards: [
    //       {
    //         awardId: 4,
    //         awardName: "Bronze",
    //         count: 44,

    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Platinum",
    //         count: 11,

    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Gold",
    //         count: 88,

    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Silver",
    //         count: 9,
    //       },
    //     ],
    //   },
    //   {
    //     region: "South",
    //     awards: [
    //       {
    //         awardId: 4,
    //         awardName: "Bronze",
    //         count: 3,

    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Platinum",
    //         count: 54,

    //       },
    //       {
    //         awardId: 45,
    //         awardName: "Gold",
    //         count: 1,

    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Silver",
    //         count: 24,
    //       },
    //     ],
    //   },
    //   {
    //     region: "East",
    //     awards: [
    //       {
    //         awardId: 4,
    //         awardName: "Bronze",
    //         count: 37,
    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Platinum",
    //         count: 53,
    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Gold",
    //         count: 1,
    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Silver",
    //         count: 64,
    //       },
    //     ],
    //   },
    //   {
    //     region: "North",
    //     awards: [
    //       {
    //         awardId: 4,
    //         awardName: "Bronze",
    //         count: 89,
    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Platinum",
    //         count: 76,
    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Gold",
    //         count: 1,
    //       },
    //       {
    //         awardId: 4,
    //         awardName: "Silver",
    //         count: 53,
    //       },
    //     ],
    //   },
    // ];

    const awardColors = {
      Platinum: "#8047E7",
      Gold: "#E7B147",
      Silver: "#BEBEBE",
      Bronze: "#E79447",
    };

    const parsedData: any = [
      {
        name: "Platinum",
        data: data.map((quarter) => {
          const award = quarter.awards.find(
            (a: AwardInterface) => a.awardName == "Platinum"
          );
          if (!award) return { count: 0, percentage: 0 };

          // Extract numeric values from "74 [74.00%]" format
          const match = award.count.toString().match(/(\d+)\s*\[(\d+.\d+)%\]/);
          return {
            name: "Platinum",
            count: match ? parseInt(match[1]) : 0,
            percentage: match ? parseFloat(match[2]) : 0,
          };
        }),
        color: awardColors.Platinum,
      },
      {
        name: "Gold",
        data: data.map((quarter) => {
          const award = quarter.awards.find(
            (a: AwardInterface) => a.awardName == "Gold"
          );
          if (!award) return { count: 0, percentage: 0 };

          // Extract numeric values from "74 [74.00%]" format
          const match = award.count.toString().match(/(\d+)\s*\[(\d+.\d+)%\]/);
          return {
            name: "Gold",
            count: match ? parseInt(match[1]) : 0,
            percentage: match ? parseFloat(match[2]) : 0,
          };
        }),
        color: awardColors.Gold,
      },
      {
        name: "Silver",
        data: data.map((quarter) => {
          const award = quarter.awards.find(
            (a: AwardInterface) => a.awardName == "Silver"
          );

          if (!award) return { count: 0, percentage: 0 };

          // Extract numeric values from "74 [74.00%]" format
          const match = award.count.toString().match(/(\d+)\s*\[(\d+.\d+)%\]/);
          return {
            name: "Silver",
            count: match ? parseInt(match[1]) : 0,
            percentage: match ? parseFloat(match[2]) : 0,
          };
        }),
        color: awardColors.Silver,
      },
      {
        name: "Bronze",
        data: data.map((quarter) => {
          const award = quarter.awards.find(
            (a: AwardInterface) => a.awardName === "Bronze"
          );

          if (!award) return { count: 0, percentage: 0 };

          // Extract numeric values from "74 [74.00%]" format
          const match = award.count.toString().match(/(\d+)\s*\[(\d+.\d+)%\]/);
          return {
            name: "Bronze",
            count: match ? parseInt(match[1]) : 0,
            percentage: match ? parseFloat(match[2]) : 0,
          };
        }),
        color: awardColors?.Bronze,
      },
    ];

    return parsedData;
  },

  getIndividualIndicatorsGraphParser: (
    res: IndividualIndicatorsGraphApiRes
  ): IndividualIndicatorsGraphParsedRes => {
    let data: IndividualIndicatorsGraphData[] = [];
    if (res && res.data && res.data.length > 0) data = res.data;

    const colorCode: { [key: string]: string } = {
      currentQuarter: "#165DFF",
      previousQuarter: "#F7BA1E",
      priorQuarter: "#14C9C9",
    };

    // Find the maximum totalScore
    // const maxTotalScore = data.reduce((max, category) => {
    //   return category.currentQuarter.totalScore > max ? category.currentQuarter.totalScore : max;
    // }, 0);

    // Filter categories with the highest totalScore
    // const highestTotalScoreCategories = data.filter(category => category.currentQuarter.totalScore === maxTotalScore);

    const categories: string[] = data.map(
      (item: IndividualIndicatorsGraphData) => item.categoryName
    );

    const seriesData: IndividualIndicatorsGraphSeriesData[] = [
      {
        name: "Current Quarter",
        data:
          data.map((item: IndividualIndicatorsGraphData) => {
            return {
              y: item.currentQuarter.totalCenters,
              percentage: item.currentQuarter.percentage,
              total: item.currentQuarter.totalCenters,
            };
          }) || [],
        color: colorCode.currentQuarter,
      },
      {
        name: "Previous Quarter",
        data:
          data.map((item: IndividualIndicatorsGraphData) => {
            return {
              y: item.previousQuarter.totalCenters,
              total: item.previousQuarter.totalCenters,
              percentage: item.previousQuarter.percentage,
            };
          }) || [],
        color: colorCode.previousQuarter,
      },
      {
        name: "Prior Quarter",
        data:
          data.map((item: IndividualIndicatorsGraphData) => {
            return {
              y: item.priorQuarter.totalCenters,
              percentage: item.priorQuarter.percentage,
              total: item.priorQuarter.totalCenters,
            };
          }) || [],
        color: colorCode.priorQuarter,
      },
    ];

    return { categories, seriesData };
  },

  getPerformanceIndicatorsGraphParser: (res: any): any => {
    let data: PerformanceIndicatorsGraphData[] = [];
    if (res && res.data && res.data.length > 0) data = res.data;

    const programmePerformanceData: any[] = [];
    const qualityAndEfficacy: any[] = [];

    // Function to extract numeric values from "73 (73.00%)" format
    const extractValues = (value: string) => {
      const match = value.toString().match(/(\d+) \((\d+.\d+)%\)/);
      return match
        ? { count: parseInt(match[1]), percentage: parseFloat(match[2]) }
        : { count: 0, percentage: 0 };
    };

    // Separating the data
    data.forEach((item: PerformanceIndicatorsGraphData) => {
      if (item.categories) {
        item.categories.forEach((parameter: any) => {
          const doingWell = extractValues(parameter?.doingWell);
          const needAttention = extractValues(parameter?.needAttention);

          const formattedData = {
            name: parameter.categoryName,
            doingWell: doingWell.count,
            doingWellPercentage: doingWell.percentage,
            needAttention: needAttention.count,
            needAttentionPercentage: needAttention.percentage,
          };

          if (item.parameterTypeName === "Programme performance") {
            programmePerformanceData.push(formattedData);
          } else if (item.parameterTypeName === "Quality and efficacy") {
            qualityAndEfficacy.push(formattedData);
          }
        });
      }
    });

    const performanceIndicatorsCategories: string[] =
      programmePerformanceData.map((item) => item.name);
    const qualityAndEfficacyCategories: string[] = qualityAndEfficacy.map(
      (item) => item.name
    );

    const performanceIndicatorsSeriesData = [
      {
        name: "Need attention",
        data: programmePerformanceData.map((item) => ({
          y: item.needAttention,
          percentage: item.needAttentionPercentage,
        })),
        color: "#E75A47",
      },
      {
        name: "Doing well",
        data: programmePerformanceData.map((item) => ({
          y: item.doingWell,
          percentage: item.doingWellPercentage,
        })),
        color: "#54C95B",
      },
    ];

    const qualityAndEfficacySeriesData = [
      {
        name: "Need attention",
        data: qualityAndEfficacy.map((item) => ({
          y: item.needAttention,
          percentage: item.needAttentionPercentage,
        })),
        color: "#E75A47",
      },
      {
        name: "Doing well",
        data: qualityAndEfficacy.map((item) => ({
          y: item.doingWell,
          percentage: item.doingWellPercentage,
        })),
        color: "#54C95B",
      },
    ];

    return {
      performanceIndicatorsCategories,
      performanceIndicatorsSeriesData,
      qualityAndEfficacyCategories,
      qualityAndEfficacySeriesData,
    };
  },

  getHeatMapTableParser: (res: HitMapTableApiRes): HitMapTableParsedRes => {
    const data =
      Array.isArray(res.data) && res.data.length > 0 && res.success
        ? res.data
        : [];

    // const data = [
    // {
    //   "centerId": 1,
    //   "centerName": "Master Nagar",
    //   "categories": {
    //     "Youth Retention % : Baseline enrolled YPs": "Need Attention",
    //     "Youth Individual Attendance: Attendance to be captured for individual attendance": "Achieved Target",
    //     "Operation Finance - Underspend": "Partial Achieved",
    //     "Youth Enrolment %": "Partial Achieved",
    //     "Female Youth Enrolment %": "Need Attention",
    //     "Youth Dropout %": "Achieved Target",
    //     "Youth Placement %": "Need Attention",
    //     "Demand Driven %": "Partial Achieved",
    //     "Operations HR - Staff Onboard: Parameter On Staff Hiring Attrition & Training": "Need Attention",
    //     "Governance & Ethics: Parameters on Centre Occupancy, Youth Attendance, Communication with FPD and Quality Documentation": "Orange",
    //     "Session Quality": "Achieved Target",
    //     "Stakeholder Relationship": "Partial Achieved",
    //     "Center Visit And Branding": "Achieved Target",
    //     "Operations HR - Staff onboard": "Partial Achieved"
    //   }
    // },
    // {
    //   "centerId": 2,
    //   "centerName": "Ambattur (Tamilnadu) - Fidelity",
    //   "categories": {
    //     "Youth Enrolment %": "Achieved Target",
    //     "Female Youth Enrolment %": "Partial Achieved",
    //     "Youth Individual Attendance": "Need Attention",
    //     "Youth Dropout %": "Partial Achieved",
    //     "Youth Placement %( sa per actual placement target for the centre)": "Need Attention",
    //     "Youth Retention %(For CWW against placement)": "Achieved Target",
    //     "Operation Finance - Underspend": "Achieved Target",
    //     "Operations HR - Staff Onboard: Parameter On Staff Hiring Attrition & Training": "Achieved Target",
    //     "Governance & Ethics: Parameters on Centre Occupancy, Youth Attendance, Communication with FPD and Quality Documentation": "Need Attention",
    //     "Session Quality": "Partial Achieved",
    //     "Stakeholder Relationship": "Need Attention",
    //     "Professionalism and Branding": "Achieved Target",
    //     "Operations HR - Staff onboard": "Achieved Target"
    //   }
    // },
    // {
    //   "centerId": 2,
    //   "centerName": "Ambattur (Tamilnadu) - Fidelity",
    //   "categories": {
    //     "Youth Enrolment %": "Achieved Target",
    //     "Female Youth Enrolment %": "Partial Achieved",
    //     "Youth Individual Attendance": "Need Attention",
    //     "Youth Dropout %": "Partial Achieved",
    //     "Youth Placement %( sa per actual placement target for the centre)": "Need Attention",
    //     "Youth Retention %(For CWW against placement)": "Achieved Target",
    //     "Operation Finance - Underspend": "Achieved Target",
    //     "Operations HR - Staff Onboard: Parameter On Staff Hiring Attrition & Training": "Achieved Target",
    //     "Governance & Ethics: Parameters on Centre Occupancy, Youth Attendance, Communication with FPD and Quality Documentation": "Need Attention",
    //     "Session Quality": "Partial Achieved",
    //     "Stakeholder Relationship": "Need Attention",
    //     "Professionalism and Branding": "Achieved Target",
    //     "Operations HR - Staff onboard": "Achieved Target"
    //   }
    // },
    // {
    //   "centerId": 2,
    //   "centerName": "Ambattur (Tamilnadu) - Fidelity",
    //   "categories": {
    //     "Youth Enrolment %": "Achieved Target",
    //     "Female Youth Enrolment %": "Partial Achieved",
    //     "Youth Individual Attendance": "Need Attention",
    //     "Youth Dropout %": "Partial Achieved",
    //     "Youth Placement %( sa per actual placement target for the centre)": "Need Attention",
    //     "Youth Retention %(For CWW against placement)": "Achieved Target",
    //     "Operation Finance - Underspend": "Achieved Target",
    //     "Operations HR - Staff Onboard: Parameter On Staff Hiring Attrition & Training": "Achieved Target",
    //     "Governance & Ethics: Parameters on Centre Occupancy, Youth Attendance, Communication with FPD and Quality Documentation": "Need Attention",
    //     "Session Quality": "Partial Achieved",
    //     "Stakeholder Relationship": "Need Attention",
    //     "Professionalism and Branding": "Achieved Target",
    //     "Operations HR - Staff onboard": "Achieved Target"
    //   }
    // },
    // {
    //   "centerId": 1,
    //   "centerName": "Master Nagar",
    //   "categories": {
    //     "Youth Retention % : Baseline enrolled YPs": "Need Attention",
    //     "Youth Individual Attendance: Attendance to be captured for individual attendance": "Achieved Target",
    //     "Operation Finance - Underspend": "Partial Achieved",
    //     "Youth Enrolment %": "Partial Achieved",
    //     "Female Youth Enrolment %": "Need Attention",
    //     "Youth Dropout %": "Achieved Target",
    //     "Youth Placement %": "Need Attention",
    //     "Demand Driven %": "Partial Achieved",
    //     "Operations HR - Staff Onboard: Parameter On Staff Hiring Attrition & Training": "Need Attention",
    //     "Governance & Ethics: Parameters on Centre Occupancy, Youth Attendance, Communication with FPD and Quality Documentation": "Orange",
    //     "Session Quality": "Achieved Target",
    //     "Stakeholder Relationship": "Partial Achieved",
    //     "Center Visit And Branding": "Achieved Target",
    //     "Operations HR - Staff onboard": "Partial Achieved"
    //   }
    // },
    // {
    //   "centerId": 1,
    //   "centerName": "Master Nagar",
    //   "categories": {
    //     "Youth Retention % : Baseline enrolled YPs": "Need Attention",
    //     "Youth Individual Attendance: Attendance to be captured for individual attendance": "Achieved Target",
    //     "Operation Finance - Underspend": "Partial Achieved",
    //     "Youth Enrolment %": "Partial Achieved",
    //     "Female Youth Enrolment %": "Need Attention",
    //     "Youth Dropout %": "Achieved Target",
    //     "Youth Placement %": "Need Attention",
    //     "Demand Driven %": "Partial Achieved",
    //     "Operations HR - Staff Onboard: Parameter On Staff Hiring Attrition & Training": "Need Attention",
    //     "Governance & Ethics: Parameters on Centre Occupancy, Youth Attendance, Communication with FPD and Quality Documentation": "Orange",
    //     "Session Quality": "Achieved Target",
    //     "Stakeholder Relationship": "Partial Achieved",
    //     "Center Visit And Branding": "Achieved Target",
    //     "Operations HR - Staff onboard": "Partial Achieved"
    //   }
    // },

    // ]

    const headerData = new Set(
      data.flatMap((info) => Object.keys(info.categories))
    );
    const uniqueHeaderName: string[] = Array.from(headerData);

    // @ts-ignore
    const rowData: { [key: string]: string }[] = data.map((info: any) => {
      return {
        centreId: info.centerId,
        centreName: info.centerName,
        ...info.categories, // Spread the categories into the object
      };
    });

    return {
      hitMapRowData: rowData,
      hitMapHeaderData: uniqueHeaderName,
    };
  },

  // getPerformanceTableParser: (res: PerformanceTableApiRes): PerformanceTableParsedRes => {

  //   let data: PerformanceTableResData[] = [];
  //   if (Array.isArray(res.data) && res.data.length > 0) data = res.data;

  //   const programmePerformanceData: FinalDataInterface[] = [];
  //   const qualityAndEfficacy: FinalDataInterface[] = [];

  //   // Separating th data of Programme performance
  //   data.forEach((item: PerformanceTableResData) => {

  //     if (item.parameters) {
  //       item.parameters.forEach((parameter: PerformanceTableDataParameters) => {
  //         if (parameter.parameterTypeName == "Programme performance") {

  //           programmePerformanceData.push({
  //             centerId: item?.centerId,

  //             centre: item.centerName || "-",
  //             region: item.region || "-",
  //             funder: item.funderName || "-",
  //             awardName: item.awardName || "-",
  //             rank: item.rank  || 0,

  //             programmePerformanceScore: parameter.sumScore || 0,
  //             finalScore: item.totalScoreAchieved || 0,

  //             parameters: parameter.parameters.map((value: DataParametersType) => {
  //               return {
  //                 parameterName: value.parameterName || "-",
  //                 scoreAchieved: value.scoreAchieved == 0 ? "-" : value.scoreAchieved,
  //                 score: value.score || 0 ,
  //                 backgroundColor: value.score == value.scoreAchieved ? "#D7FFE0" :
  //                                  value.scoreAchieved < value.score && value.scoreAchieved !== 0 ? "#FFBC57" :
  //                                  value.scoreAchieved == 0 ? "#FF5757" : "",

  //               };
  //             }) || []
  //           });
  //         }
  //       });
  //     }
  //   });

  //   //  Separating th data of Quality and efficacy
  //   data.forEach((item: PerformanceTableResData) => {

  //     if (item.parameters) {
  //       item.parameters.forEach((parameter: PerformanceTableDataParameters ) => {
  //         if (parameter.parameterTypeName == "Quality and efficacy") {
  //           qualityAndEfficacy.push({
  //             centerId: item?.centerId,
  //             centre: item.centerName || "-",
  //             region: item.region || "-",
  //             funder: item.funderName || "-",
  //             awardName: item.awardName || "-",
  //             rank: item.rank || 0,

  //             programmePerformanceScore: parameter.sumScore || 0,
  //             finalScore: item.totalScoreAchieved || 0 ,

  //             parameters: parameter.parameters.map((value: DataParametersType) => {
  //               return {
  //                 parameterName: value.parameterName || "-",
  //                 scoreAchieved: value.scoreAchieved == 0 ? "-" : value.scoreAchieved,
  //                 score: value.score || "",
  //                 backgroundColor: value.score == value.scoreAchieved ? "#D7FFE0" :
  //                                  value.scoreAchieved < value.score && value.scoreAchieved !== 0 ? "#FFBC57" :
  //                                  value.scoreAchieved == 0 ? "#FF5757" : "",
  //               };

  //             }) || []
  //           });
  //         }
  //       });
  //     }
  //   });

  //   return {programmePerformanceData, qualityAndEfficacy}
  // },

  getPerformanceTableParser: (res: PerformanceTableApiRes): any => {
    let data: any[] = [];

    if (res?.data?.length > 0) {
      data = res?.data?.map((entry: any) => ({
        centerId: entry?.centerId,
        centerName: entry?.centerName,
        region: entry?.region,
        funderName: entry?.funderName,
        awardName: entry?.awardName,
        totalSumScore: parseFloat(entry?.totalScore) || 0,
        totalSumScoreAchieved: parseFloat(entry?.totalScoreAchieved) || 0,
        rank: parseInt(entry?.rank, 10) || 0,
        parameters:
          entry?.parameters?.map((param: any) => ({
            sumScore: parseFloat(param?.sumScore) || 0,
            sumScoreAchieved: parseFloat(param?.sumScoreAchieved) || 0,
            parameterTypeName: param?.parameterTypeName || "",
            parameterTypeId: param?.parameterTypeId || "",
            parameterDetails:
              param.parameters?.map((detail: any) => ({
                score: parseFloat(detail?.score) || 0,
                scoreAchieved: parseFloat(detail?.scoreAchieved) || 0,
                parameterName: detail?.parameterName || "",
                backgroundColor:
                  detail.score == detail.scoreAchieved
                    ? "#D7FFE0"
                    : detail.scoreAchieved < detail.score &&
                      detail.scoreAchieved !== 0
                    ? "#FFBC57"
                    : detail.scoreAchieved == 0
                    ? "#FF5757"
                    : "",
              })) || [],
          })) || [],
      }));
    }

    return {
      performanceTableRes: data,
      totalEntries: data.length,
    };
  },
};

export default dashboardParser;
